/** SAM — ported from the approved `helper_character.html`, unchanged.
 *
 *  Same SVG, same gradients (#5fb4ff → #1e6fd9 → #0f3f94 body, #e8650f →
 *  #f28a16 → #ffc15a tuft), same glow filters, same moods, blink, breathing,
 *  eye-following and talking mouth. Nothing about him is redesigned here: the
 *  only change is that the loop lives in a React effect and the moods are told
 *  to him by the panel instead of by buttons.
 *
 *  One requestAnimationFrame loop writing attributes on refs — no React state
 *  per frame, so the portal beside him is not slowed. With
 *  `prefers-reduced-motion` he holds still, exactly as the approved file does.
 */
import React, { useEffect, useRef } from 'react';

// Straight from the approved file.
const MOODS = {
    calm: { up: 4, low: 7, w: 11, browY: 0, tl: 0, tr: 0, look: [0, 0], cheek: 0.22, arm: 0, dots: 0 },
    happy: { up: 2, low: 12, w: 14, browY: -4, tl: 0, tr: 0, look: [0, 0], cheek: 0.42, arm: 0, dots: 0 },
    thinking: { up: 5, low: 5, w: 8, browY: -3, tl: -8, tr: 6, look: [0.7, -0.8], cheek: 0.2, arm: 0, dots: 1 },
    concerned: { up: -1, low: -4, w: 11, browY: -1, tl: -16, tr: 16, look: [0, 0.1], cheek: 0.12, arm: 0, dots: 0 },
    celebrate: { up: 0, low: 15, w: 16, browY: -6, tl: 0, tr: 0, look: [0, -0.2], cheek: 0.5, arm: -1, dots: 0 },
};

const lerp = (a, b, k) => a + (b - a) * k;

export const SamCharacter = ({ mood = 'calm', speaking = false, size = 'panel',
                               onTap, name = 'Sam', className = '' }) => {
    const refs = useRef({});
    const live = useRef({ mood, speaking });
    live.current = { mood: MOODS[mood] ? mood : 'calm', speaking };

    useEffect(() => {
        const reduce = window.matchMedia
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const el = refs.current;
        if (!el.root) return undefined;
        const cur = { up: 4, low: 7, w: 11, browY: 0, tl: 0, tr: 0, cheek: 0.22, dots: 0, lx: 0, ly: 0 };
        const ptr = { x: 0, y: 0, has: false };
        let parts = [];
        let pulse = 0; let mouthOpen = 0; let blink = 0; let nextBlink = 1.6;
        let t = 0; let bounce = 0; let lastMood = live.current.mood; let raf = 0;

        const burst = () => {
            for (let i = 0; i < 14; i += 1) {
                const a = Math.random() * Math.PI * 2;
                const s = 40 + Math.random() * 60;
                parts.push({ x: 0, y: -20, vx: Math.cos(a) * s, vy: Math.sin(a) * s - 40,
                             life: 1, c: i % 2 ? '#ff8a1f' : '#3b8bff' });
            }
        };
        const track = (e) => {
            const svg = el.svg;
            if (!svg) return;
            const r = svg.getBoundingClientRect();
            const p = e.touches ? e.touches[0] : e;
            ptr.x = (p.clientX - (r.left + r.width / 2)) / (window.innerWidth * 0.5);
            ptr.y = (p.clientY - (r.top + r.height / 2)) / (window.innerHeight * 0.5);
            ptr.has = true;
        };
        window.addEventListener('pointermove', track, { passive: true });
        window.addEventListener('touchstart', track, { passive: true });

        let last = performance.now();
        const frame = (now) => {
            const dt = Math.min(0.05, (now - last) / 1000);
            last = now; t += dt;
            const state = live.current;
            const M = MOODS[state.mood] || MOODS.calm;
            if (state.mood !== lastMood) {
                lastMood = state.mood;
                if (state.mood === 'celebrate' && !reduce) { bounce = 1; burst(); }
            }
            const k = Math.min(1, dt * 7);
            cur.up = lerp(cur.up, M.up, k); cur.low = lerp(cur.low, M.low, k);
            cur.w = lerp(cur.w, M.w, k); cur.browY = lerp(cur.browY, M.browY, k);
            cur.tl = lerp(cur.tl, M.tl, k); cur.tr = lerp(cur.tr, M.tr, k);
            cur.cheek = lerp(cur.cheek, M.cheek, k); cur.dots = lerp(cur.dots, M.dots, k);

            // the mouth while he talks
            let target = 0;
            if (state.speaking) {
                target = (0.35 + 0.35 * Math.abs(Math.sin(t * 13))
                          + 0.3 * Math.abs(Math.sin(t * 7.3))) * (0.55 + 0.45 * pulse);
                pulse = Math.max(0, pulse - dt * 3);
            }
            mouthOpen = lerp(mouthOpen, target, Math.min(1, dt * 22));
            const up = cur.up - mouthOpen * 9;
            const low = cur.low + mouthOpen * 16;
            const w = cur.w - mouthOpen * 2.5;
            el.mouth.setAttribute('d', `M ${-w} 0 Q 0 ${up.toFixed(1)} ${w} 0 Q 0 ${low.toFixed(1)} ${-w} 0 Z`);

            // blink
            nextBlink -= dt;
            if (nextBlink <= 0) { blink = 1; nextBlink = 2 + Math.random() * 3.5; }
            if (blink > 0) blink = Math.max(0, blink - dt * 7);
            const lid = reduce ? 1
                : (blink > 0 ? (1 - Math.sin(Math.min(1, 1 - blink) * Math.PI) * 0.92) : 1);
            el.eyeL.setAttribute('transform', `translate(-28 -10) scale(1 ${lid.toFixed(2)})`);
            el.eyeR.setAttribute('transform', `translate(28 -10) scale(1 ${lid.toFixed(2)})`);

            // eyes follow the pointer; a mood may set its own gaze
            const gx = ptr.has ? Math.max(-1, Math.min(1, ptr.x * 1.6)) : 0;
            const gy = ptr.has ? Math.max(-1, Math.min(1, ptr.y * 1.6)) : 0;
            const named = M.look[0] !== 0 || M.look[1] !== 0;
            cur.lx = lerp(cur.lx, named ? M.look[0] : gx, Math.min(1, dt * 9));
            cur.ly = lerp(cur.ly, named ? M.look[1] : gy, Math.min(1, dt * 9));
            const pt = `translate(${(cur.lx * 6).toFixed(1)} ${(cur.ly * 5).toFixed(1)})`;
            el.pupL.setAttribute('transform', pt); el.pupR.setAttribute('transform', pt);
            el.browL.setAttribute('transform', `translate(-28 ${(-38 + cur.browY).toFixed(1)}) rotate(${cur.tl.toFixed(1)})`);
            el.browR.setAttribute('transform', `translate(28 ${(-38 + cur.browY).toFixed(1)}) rotate(${cur.tr.toFixed(1)})`);
            el.cheekL.setAttribute('opacity', cur.cheek.toFixed(2));
            el.cheekR.setAttribute('opacity', cur.cheek.toFixed(2));
            el.dots.setAttribute('opacity', cur.dots.toFixed(2));

            // body: breathe, float, hop
            const breathe = reduce ? 0 : Math.sin(t * 2) * 0.012;
            const bob = reduce ? 0 : Math.sin(t * 1.4) * 3;
            bounce = Math.max(0, bounce - dt * 1.3);
            const hop = -Math.abs(Math.sin(bounce * Math.PI * 3)) * bounce * 22;
            const tilt = reduce ? 0
                : Math.sin(t * 0.9) * 1.6 + (state.mood === 'thinking' ? -5 : 0);
            el.root.setAttribute('transform', `translate(0 ${(bob + hop).toFixed(1)}) rotate(${tilt.toFixed(1)} 0 80) scale(${(1 + breathe).toFixed(3)} ${(1 - breathe).toFixed(3)})`);
            el.tuft.setAttribute('transform', `rotate(${(reduce ? 0 : Math.sin(t * 3) * 3 + (state.speaking ? Math.sin(t * 14) * 3 : 0)).toFixed(1)} 0 -68)`);

            // arms
            const wave = M.arm < 0 ? -(60 + Math.sin(t * 12) * 14)
                : (reduce ? 0 : Math.sin(t * 1.4) * 3);
            el.armL.setAttribute('transform', `translate(-76 18) rotate(${(M.arm < 0 ? wave : -wave * 0.4).toFixed(1)} 0 0)`);
            el.armR.setAttribute('transform', `translate(76 18) rotate(${(M.arm < 0 ? -wave : wave * 0.4).toFixed(1)} 0 0)`);

            // celebration particles
            let html = '';
            for (let i = parts.length - 1; i >= 0; i -= 1) {
                const p = parts[i];
                p.life -= dt * 1.1; p.vy += 120 * dt; p.x += p.vx * dt; p.y += p.vy * dt;
                if (p.life <= 0) { parts.splice(i, 1); } else {
                    html += `<circle cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="${(2 + p.life * 3).toFixed(1)}" fill="${p.c}" opacity="${p.life.toFixed(2)}"/>`;
                }
            }
            el.particles.innerHTML = html;
            raf = requestAnimationFrame(frame);
        };
        raf = requestAnimationFrame(frame);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('pointermove', track);
            window.removeEventListener('touchstart', track);
            parts = [];
        };
    }, []);

    const set = (key) => (node) => { refs.current[key] = node; };
    const slim = size === 'slim';
    return (
        <span role={onTap ? 'button' : undefined} tabIndex={onTap ? 0 : undefined}
              onClick={onTap} data-testid={`sam-${size}`}
              aria-label={onTap ? name : undefined}
              onKeyDown={onTap ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTap(); }
              } : undefined}
              className={`inline-block ${onTap ? 'cursor-pointer' : ''} ${className}`}>
            <svg ref={set('svg')} className="block w-full h-full"
                 style={{ overflow: slim ? 'hidden' : 'visible' }}
                 viewBox={slim ? '-62 -78 124 124' : '-110 -135 220 250'}
                 xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                <defs>
                    <radialGradient id="samBlue" cx="38%" cy="28%" r="85%">
                        <stop offset="0" stopColor="#5fb4ff" />
                        <stop offset=".55" stopColor="#1e6fd9" />
                        <stop offset="1" stopColor="#0f3f94" />
                    </radialGradient>
                    <linearGradient id="samBlueArm" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0" stopColor="#3f95f0" />
                        <stop offset="1" stopColor="#1457b8" />
                    </linearGradient>
                    <linearGradient id="samOr" x1="0" y1="1" x2="0" y2="0">
                        <stop offset="0" stopColor="#e8650f" />
                        <stop offset=".5" stopColor="#f28a16" />
                        <stop offset="1" stopColor="#ffc15a" />
                    </linearGradient>
                </defs>
                <g ref={set('particles')} />
                <g ref={set('root')} transform="translate(0 -4.5) rotate(-0.3 0 80) scale(0.995 1.005)">
                    <ellipse cx="0" cy="92" rx="52" ry="7" fill="#000" opacity=".35" />
                    <g ref={set('armL')} transform="translate(-76 18)">
                        <ellipse cx="-4" cy="10" rx="9" ry="16" fill="url(#samBlueArm)"
                                 stroke="#b9dcff" strokeWidth="3" className="sam-glow-b" />
                    </g>
                    <g ref={set('armR')} transform="translate(76 18)">
                        <ellipse cx="4" cy="10" rx="9" ry="16" fill="url(#samBlueArm)"
                                 stroke="#b9dcff" strokeWidth="3" className="sam-glow-b" />
                    </g>
                    <path d="M -26 82 Q -26 96 -12 96 Q 0 96 0 84" fill="#1a62c8"
                          stroke="#b9dcff" strokeWidth="3" className="sam-glow-b" />
                    <path d="M 26 82 Q 26 96 12 96 Q 0 96 0 84" fill="#1a62c8"
                          stroke="#b9dcff" strokeWidth="3" className="sam-glow-b" />
                    <g ref={set('tuft')} className="sam-glow-o" fill="none" stroke="url(#samOr)"
                       strokeLinecap="round" strokeLinejoin="round" transform="rotate(-1.7 0 -68)">
                        <path d="M -9 -68 C -26 -84 -20 -100 -34 -110" strokeWidth="7" />
                        <path d="M -1 -69 C -10 -92 4 -106 -6 -128" strokeWidth="8" />
                        <path d="M 8 -68 C 18 -86 32 -90 42 -104" strokeWidth="7" />
                    </g>
                    <path d="M 0 -70 C 56 -70 80 -30 80 14 C 80 62 46 86 0 86 C -46 86 -80 62 -80 14 C -80 -30 -56 -70 0 -70 Z"
                          fill="url(#samBlue)" stroke="#b9dcff" strokeWidth="3.5"
                          strokeLinejoin="round" className="sam-glow-b" />
                    <path d="M -50 -34 Q -34 -52 -14 -56" fill="none" stroke="#e8f1ff"
                          strokeWidth="2.5" strokeLinecap="round" opacity=".35" />
                    <circle ref={set('cheekL')} cx="-47" cy="22" r="9" fill="#ffb04a" opacity="0.22" />
                    <circle ref={set('cheekR')} cx="47" cy="22" r="9" fill="#ffb04a" opacity="0.22" />
                    <g ref={set('eyeL')} transform="translate(-28 -10) scale(1 1)">
                        <circle r="17" fill="#0a1226" stroke="#dff0ff" strokeWidth="3" className="sam-glow" />
                        <g ref={set('pupL')}>
                            <circle r="9" fill="#e8f1ff" />
                            <circle cx="-3" cy="-3.5" r="3" fill="#3b8bff" />
                        </g>
                    </g>
                    <g ref={set('eyeR')} transform="translate(28 -10) scale(1 1)">
                        <circle r="17" fill="#0a1226" stroke="#dff0ff" strokeWidth="3" className="sam-glow" />
                        <g ref={set('pupR')}>
                            <circle r="9" fill="#e8f1ff" />
                            <circle cx="-3" cy="-3.5" r="3" fill="#3b8bff" />
                        </g>
                    </g>
                    <path ref={set('browL')} d="M -11 0 Q 0 -6 11 0" fill="none" stroke="#e8f1ff"
                          strokeWidth="3.5" strokeLinecap="round" transform="translate(-28 -38)" />
                    <path ref={set('browR')} d="M -11 0 Q 0 -6 11 0" fill="none" stroke="#e8f1ff"
                          strokeWidth="3.5" strokeLinecap="round" transform="translate(28 -38)" />
                    <path ref={set('mouth')} d="M -11 0 Q 0 4.0 11 0 Q 0 7.0 -11 0 Z" fill="#06101f"
                          stroke="#e8f1ff" strokeWidth="3" strokeLinejoin="round"
                          strokeLinecap="round" transform="translate(0 40)" />
                    <g ref={set('dots')} opacity="0">
                        <circle className="sam-spark" cx="62" cy="-86" r="5" fill="#e8f1ff" />
                        <circle className="sam-spark" cx="80" cy="-96" r="5" fill="#e8f1ff"
                                style={{ animationDelay: '.25s' }} />
                        <circle className="sam-spark" cx="98" cy="-106" r="5" fill="#e8f1ff"
                                style={{ animationDelay: '.5s' }} />
                    </g>
                </g>
            </svg>
        </span>
    );
};

export default SamCharacter;
